import { useState, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ExternalLink, Plus, Users, Database, Building2, MessagesSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSubscription } from "@/hooks/useSubscription";
import { useMacQuota } from "@/hooks/useMacQuota";
import { useMacOveragePurchase } from "@/hooks/useMacOveragePurchase";
import {
  plans,
  addOns,
  isStripeTestMode,
  addOnPriceIdForCycle,
  BILLING_CYCLE_LABELS,
  CYCLE_MONTHS,
  CYCLE_UNIT,
} from "@/config/stripePlans";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import BillingUsageMeter from "./BillingUsageMeter";

const date = (value: string | Date | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
};

function SectionHeading({ id, children }: { id: string; children: ReactNode }) {
  return <div className="mb-4 flex items-center gap-4">
    <h2 id={id} className="shrink-0 font-heading text-base font-semibold text-foreground">{children}</h2>
    <div className="h-px flex-1 bg-border" aria-hidden="true" />
  </div>;
}

const AccountBillingAndPlan = () => {
  const { businessId, hasBookingsAddon, refreshBusinessContext } = useAuth();
  const { outlets } = useOutlet();
  const queryClient = useQueryClient();
  const [addonConfirmOpen, setAddonConfirmOpen] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { subscriptionTier, billingCycleType, nextBillingDate, subscriptionStatus, stripeCustomerId, loading: subLoading, refresh } = useSubscription();
  const currentPlan = (subscriptionTier || "free").toLowerCase();
  const plan = plans[currentPlan];
  const storageLimit = plan ? plan.limits.contacts_storage : undefined;
  // Bookings bills on the plan's cycle (S$20/mo or S$60/quarter). There is no
  // yearly Bookings price, so a yearly plan can't add it self-serve.
  const bookingsCycle = billingCycleType ?? "monthly";
  const bookingsPriceId = addOnPriceIdForCycle(addOns.bookings, bookingsCycle);
  const bookingsCost = bookingsCycle === "yearly"
    ? null
    : `S$${(addOns.bookings?.price.monthly ?? 20) * CYCLE_MONTHS[bookingsCycle]}/${CYCLE_UNIT[bookingsCycle]}`;
  const isComplimentaryTrial = subscriptionStatus === "trialing" && !stripeCustomerId;
  const trialDaysRemaining = isComplimentaryTrial && nextBillingDate
    ? Math.max(0, Math.ceil((new Date(nextBillingDate).getTime() - Date.now()) / 86_400_000)) : null;
  const hasBillableSubscription = !!stripeCustomerId && (subscriptionStatus === "active" || subscriptionStatus === "trialing");
  const macQuota = useMacQuota(businessId);
  const { purchase: purchaseOverage, isPurchasing: overageLoading } = useMacOveragePurchase(macQuota.refetch);
  const quotaLimits = plans[macQuota.planKey ?? ""]?.limits;
  const packSize = quotaLimits?.mac_overage_pack_size;
  const packPrice = quotaLimits?.mac_overage_pack_price_usd;
  const atCeiling = macQuota.macCeiling !== null && macQuota.effectiveLimit !== null && macQuota.effectiveLimit >= macQuota.macCeiling;

  const { data: usage, isLoading: usageLoading, isError: usageError } = useQuery({
    queryKey: ["billing-usage", businessId],
    enabled: !!businessId,
    staleTime: 30000,
    queryFn: async () => {
      const [contacts, staff] = await Promise.all([
        supabase.from("contacts").select("*", { count: "exact", head: true }).eq("business_id", businessId!),
        supabase.from("staff_members").select("*", { count: "exact", head: true }).eq("business_id", businessId!).in("status", ["active", "pending"]),
      ]);
      if (contacts.error) throw contacts.error;
      if (staff.error) throw staff.error;
      return { contacts: contacts.count ?? 0, staff: staff.count ?? 0 };
    },
  });

  // Check for checkout success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (checkout === "success") {
      toast({ title: "Subscription activated!", description: "Your plan has been upgraded successfully." });
      refresh();
      void refreshBusinessContext();
      queryClient.invalidateQueries({ queryKey: ['billing-usage', businessId] });
      queryClient.invalidateQueries({ queryKey: ['businessSidebar', businessId] });
      window.history.replaceState({}, "", window.location.pathname + "?tab=settings&settings=billing");
    } else if (checkout === "cancel") {
      toast({ title: "Checkout cancelled", description: "You can upgrade anytime.", variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname + "?tab=settings&settings=billing");
    }
  }, [refresh, refreshBusinessContext, businessId, queryClient]);

  // Add the Dibs Bookings add-on as an additive subscription item (prorated +
  // invoiced now) via the dedicated manage-bookings-addon edge fn. Requires an
  // existing active/trialing subscription (Builder, Starter, …).
  const handleAddBookingsAddon = async () => {
    if (!hasBillableSubscription) return;
    const priceId = bookingsPriceId;
    if (!priceId) {
      toast({ title: "Dibs Bookings isn't available on yearly billing", description: "Contact us and we'll add it for you.", variant: "destructive" });
      return;
    }
    setAddonLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-bookings-addon", {
        body: { priceId, test_mode: isStripeTestMode },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAddonConfirmOpen(false);
      toast({ title: "Dibs Bookings added", description: data?.message || "Your bookings workspace is unlocked." });
      await refresh();
      // has_bookings_addon lives in AuthContext; refresh the page so the gate +
      // sidebar pick it up immediately.
      queryClient.invalidateQueries();
      setTimeout(() => window.location.reload(), 600);
    } catch (err: any) {
      toast({ title: "Couldn't add Dibs Bookings", description: err.message || "Please try again.", variant: "destructive" });
    } finally {
      setAddonLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { test_mode: isStripeTestMode }
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error("Billing management is unavailable. Please try again.");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to open subscription management", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  if (subLoading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading billing details" /></div>;

  return (
    <main className="space-y-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h1 className="text-xl font-heading font-semibold text-foreground">Billing and plan</h1>
          <p className="text-sm text-muted-foreground">Manage your subscription, track usage, and find the right plan for your business.</p>
        </div>
      </header>

      <section aria-labelledby="billing-subscription-heading">
        <SectionHeading id="billing-subscription-heading">Current subscription</SectionHeading>
        <div className="rounded-xl border bg-card p-4 sm:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-heading text-xl font-semibold">{plan?.name ?? subscriptionTier ?? "Current"} plan</h3>
              <Badge variant="outline" className={subscriptionStatus === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : ""}>
                {isComplimentaryTrial ? "Free trial" : subscriptionStatus === "trialing" ? "Trial" : subscriptionStatus?.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase()) || "No subscription"}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2">
            {stripeCustomerId && (
              <Button variant="outline" size="sm" className="standard-action-button shrink-0" onClick={handleManageSubscription} disabled={portalLoading}>
                {portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                Manage billing
              </Button>
            )}
            <Button asChild size="sm" className="standard-action-button">
              <a href="https://www.usedibs.com/#pricing" target="_blank" rel="noopener noreferrer">
                Upgrade plan <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="billing-cycle">Billing cycle</Label>
              <Input
                id="billing-cycle"
                value={isComplimentaryTrial ? "No payment during trial" : billingCycleType ? BILLING_CYCLE_LABELS[billingCycleType] : "—"}
                readOnly
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="billing-next-date">{subscriptionStatus === "trialing" ? "Trial ends" : "Next billing date"}</Label>
              <Input id="billing-next-date" value={date(nextBillingDate)} readOnly />
            </div>
          </div>
          {isComplimentaryTrial ? <div role="status" className="mt-5 flex flex-col gap-3 rounded-lg bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-6">You’re currently on a Premium trial{trialDaysRemaining !== null && Number.isFinite(trialDaysRemaining) ? ` with ${trialDaysRemaining} ${trialDaysRemaining === 1 ? "day" : "days"} remaining` : ""}. View pricing to choose your next plan.</p>
          </div> : stripeCustomerId ? <p className="mt-4 text-xs leading-5 text-muted-foreground">Your current subscription follows your existing agreement. Manage billing to view invoices, payment details, and subscription options.</p> : null}
        </div>
        <section aria-labelledby="billing-addons-heading" className="mt-5">
          <h3 id="billing-addons-heading" className="mb-3 font-heading text-sm font-semibold">Add-ons</h3>
          <div className="admin-data-table-shell">
            <Table className="admin-data-table">
              <caption className="sr-only">Subscription add-ons</caption>
              <TableHeader className="admin-data-table-header">
                <TableRow className="hover:bg-transparent [&>th]:h-10 [&>th]:border-b [&>th]:border-border [&>th]:bg-card">
                  <TableHead scope="col" className="w-1/2 px-3">Name</TableHead>
                  <TableHead scope="col" className="px-3">Cost</TableHead>
                  <TableHead scope="col" className="w-24 px-3 text-right">Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="admin-data-table-body">
                <TableRow className="border-0">
                  <TableCell className="admin-data-table-cell font-medium">Dibs Bookings</TableCell>
                  <TableCell className="admin-data-table-cell">{bookingsCost ?? "Not on yearly billing"}</TableCell>
                  <TableCell className="admin-data-table-cell text-right">
                    <div className="flex justify-end">
                      <Switch
                        aria-label="Dibs Bookings added"
                        checked={hasBookingsAddon}
                        disabled={addonLoading || portalLoading || (hasBookingsAddon ? !stripeCustomerId : !hasBillableSubscription || !bookingsPriceId)}
                        title={hasBookingsAddon
                          ? stripeCustomerId ? "Manage this add-on in billing" : "Included in your plan"
                          : !hasBillableSubscription ? "Choose a plan first"
                          : bookingsPriceId ? "Add Dibs Bookings" : "Not available on yearly billing"}
                        onCheckedChange={(checked) => {
                          if (checked) setAddonConfirmOpen(true);
                          else void handleManageSubscription();
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </section>
      </section>

      <section aria-labelledby="billing-usage-heading" id="billing-usage">
        <SectionHeading id="billing-usage-heading">Usage</SectionHeading>
        {usageError ? <p className="text-sm text-destructive">Could not load usage. Please refresh to try again.</p> : usageLoading || macQuota.isLoading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading usage" /> : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <BillingUsageMeter label="Monthly engaged customers" icon={MessagesSquare} used={macQuota.macUsed} limit={macQuota.isUnlimited ? null : macQuota.effectiveLimit ?? undefined} />
            <BillingUsageMeter label="Stored contacts" icon={Database} used={usage?.contacts ?? 0} limit={storageLimit} />
            <BillingUsageMeter label="Staff accounts" icon={Users} used={usage?.staff ?? 0} limit={plan?.limits.staff} />
            <BillingUsageMeter label="Branches" icon={Building2} used={outlets.filter((outlet) => outlet.is_active !== false).length} limit={plan?.limits.outlets} />
        </div>}
        {!usageLoading && !usageError && !macQuota.isLoading && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">Staff usage includes pending invitations and excludes the owner.</p>
              {packSize != null && packPrice != null && <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <p className="text-xs text-muted-foreground">Additional capacity for your current plan.</p>
                <Button variant="outline" size="sm" className="standard-action-button text-xs" disabled={overageLoading || atCeiling || !hasBillableSubscription} onClick={() => purchaseOverage(1)}>
                  {overageLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
                  {atCeiling ? "Limit reached" : `Add ${packSize} (S$${packPrice})`}
                </Button>
              </div>}

          </div>
        )}
      </section>

      <AlertDialog open={addonConfirmOpen} onOpenChange={setAddonConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Dibs Bookings ({bookingsCost})?</AlertDialogTitle>
            <AlertDialogDescription>This adds the Dibs Bookings add-on to your current subscription as a separate line item. You'll be charged a pro-rated amount for the rest of this billing period now, then {bookingsCost} going forward. Your bookings workspace and storefront unlock immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={addonLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); void handleAddBookingsAddon(); }} disabled={addonLoading}>
              {addonLoading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Add Dibs Bookings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default AccountBillingAndPlan;
